/**
 * CopyableValue — canonical design-system click-to-copy value (src/ui/ds).
 *
 * A quiet value that copies itself to the clipboard on click: faded copy icon
 * that expands into a neutral chip on hover, presses in on click, then flashes
 * green with a check on copy. The value truncates with an ellipsis so a long
 * string (e.g. a UUID) never stretches a width-constrained host such as a table
 * cell.
 *
 * Interaction states (all real on the live element):
 *   - default  quiet tertiary text, transparent
 *   - hover    neutral chip (bg.hover), secondary ink, padding expands
 *   - pressed  the hover chip pressed in (scale 0.96)
 *   - copied   green flash (bg.success) + check, held ~1.4s after a copy
 * `forceState` (used by the gallery) pins one of these for inspection.
 *
 * When an `href` is present, a small external-link button sits beside the copy
 * pill and opens it in a new tab. Both controls stop click propagation so they
 * never trigger a surrounding row/card click (e.g. a drawer).
 *
 *   - font: mono (default — ids, tokens, hashes) | body (human-readable values)
 *   - copyText: copy a different string than the one displayed
 *
 * Atom: built only from primitives (Flex + chakra.button/a + Icon). Used by the
 * Identities/Segments External ID column. Theme-token driven.
 *
 *
 * PORTED VERBATIM from ploy-client src/ui/ds/CopyableValue.tsx — the canonical
 * design-system component. Do not restyle here; upstream owns it. Portal-local
 * changes only: dsTag inlined, viewer-only `whenToUse` export dropped.
 *
 * Location: src/components/ds/CopyableValue.tsx
 */
import { chakra, Flex, Icon } from "@chakra-ui/react";
import { useState } from "react";
import { LuCopy, LuCheck, LuExternalLink } from "react-icons/lu";

/* dev-only DOM marker (inlined from ploy-client's ds/dsTag) — compiles away
   in production builds. */
const dsTag = (name: string): Record<string, string> =>
  import.meta.env.DEV ? { "data-ds": name } : {};

export type CopyableValueFont = "mono" | "body";
export type CopyableValueState = "default" | "hover" | "pressed" | "copied";

export interface CopyableValueProps {
  /** the value shown and — unless `copyText` is set — copied to the clipboard */
  value: string;
  /** copy a different string than the displayed `value` */
  copyText?: string;
  /** display font: `mono` for ids/tokens (default), `body` for human-readable text */
  font?: CopyableValueFont;
  /** optional trailing link — renders an external-link button that opens in a new tab */
  href?: string | null;
  /** tooltip / aria-label for the copy button (e.g. "Copy external ID") */
  copyLabel?: string;
  /** tooltip / aria-label for the trailing link button */
  linkLabel?: string;
  /** pin a visual state for the gallery; runtime omits it (real :hover/:active apply) */
  forceState?: CopyableValueState;
}

type Style = Record<string, unknown>;

// Per-state copy-chip treatment. default is the quiet resting pill; hover surfaces
// the neutral chip and expands the padding; pressed presses that chip in; copied is
// the green confirmation flash.
const REST: Style = { bg: "transparent", color: "text.body.tertiary" };
const HOVER: Style = { bg: "bg.hover", color: "text.body.secondary", px: "1xs" };
const PRESSED: Style = { bg: "bg.hover", color: "text.body.primary", px: "1xs", transform: "scale(0.96)" };
const COPIED: Style = { bg: "bg.success", color: "text.body.success" };

const LinkButton = ({ href, label }: { href: string; label: string }) => (
  <chakra.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    title={label}
    aria-label={label}
    onClick={(e) => e.stopPropagation()}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    w="22px"
    h="22px"
    borderRadius="component.sm"
    color="text.body.tertiary"
    cursor="pointer"
    transition="background 120ms, color 120ms, transform 0.1s ease"
    _hover={{ bg: "bg.hover", color: "text.body.secondary" }}
    _active={{ transform: "scale(0.92)" }}
  >
    <Icon as={LuExternalLink} boxSize="13px" />
  </chakra.a>
);

export function CopyableValue({
  value,
  copyText,
  font = "mono",
  href,
  copyLabel = "Copy",
  linkLabel = "Open link",
  forceState,
}: CopyableValueProps) {
  const [copiedLive, setCopiedLive] = useState(false);
  const copied = copiedLive || forceState === "copied";
  // a pinned hover/pressed/copied state suppresses the live :hover / :active.
  const forced = forceState != null && forceState !== "default";

  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = copyText ?? value;
    if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
    setCopiedLive(true);
    window.setTimeout(() => setCopiedLive(false), 1400);
  };

  const applied: Style = copied
    ? COPIED
    : forceState === "hover"
      ? HOVER
      : forceState === "pressed"
        ? PRESSED
        : REST;

  return (
    <Flex {...dsTag("CopyableValue")} align="center" gap="2xs" minW={0} maxW="100%">
      <chakra.button
        type="button"
        title={copied ? "Copied!" : copyLabel}
        aria-label={copied ? "Copied!" : copyLabel}
        onClick={onCopy}
        display="inline-flex"
        alignItems="center"
        gap="2xs"
        minW={0}
        border="0"
        cursor="pointer"
        fontFamily={font}
        fontSize="xs"
        borderRadius="component.sm"
        pl="0"
        pr="1xs"
        py="2px"
        transition="background 120ms, color 120ms, padding 120ms, transform 0.1s ease"
        _hover={!copied && !forced ? HOVER : undefined}
        _active={!copied && !forced ? PRESSED : undefined}
        {...applied}
      >
        <chakra.span overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {value}
        </chakra.span>
        <chakra.span
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="12px"
          h="12px"
          flexShrink={0}
          opacity={copied ? 1 : 0.7}
          color={copied ? "success.600" : undefined}
        >
          <Icon as={copied ? LuCheck : LuCopy} boxSize="12px" />
        </chakra.span>
      </chakra.button>
      {href && <LinkButton href={href} label={linkLabel} />}
    </Flex>
  );
}

export default CopyableValue;
